import React from "react";
import { useTranslation } from "react-i18next";
import { Allocation, allocation, id } from "@planzone/dto";
import { TextInput } from "@common/textInput";
import { TaskStatus } from "@scenes/taskStatus";
import { ListSelect } from "@common/listSelect";
import { ProjectsSelection } from "@common/projectsSelection";
import { RichTextInput } from "@common/richTextInput";
import { ParticipantsList } from "./participantsList";
import { DateTimePicker } from "@common/dateTimePicker";
import { Props } from "./types";
import styles from "./styles.scss";

function TaskCreationForm({
  formData,
  project,
  lists,
  list,
  error,
  isMultiProject,
  editForm,
  setList,
  onProjectChange,
}: Props) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = React.useState(formData.startDate);
  const [endDate, setEndDate] = React.useState(formData.endDate);
  const start = new Date(startDate || new Date());
  const end = new Date(endDate || new Date());

  React.useEffect(() => {
    setStartDate(formData.startDate);
  }, [formData.startDate]);

  React.useEffect(() => {
    setEndDate(formData.endDate);
  }, [formData.endDate]);

  const editAllocation = (allocations: Allocation[]) => {
    editForm({
      ...formData,
      allocations: allocations.map((allocation) => ({
        ...allocation,
        uuid: id,
      })),
    });
  };

  const toDateType = (dateStr: string) => {
    const d = new Date(dateStr);
    return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
  };

  return (
    <div className={styles.taskCreationForm}>
      {isMultiProject && (
        <div className={styles.projectSelectionRow}>
          <div className={styles.group}>
            <h3 className={styles.title}>
              {t("taskCreation.taskCreationForm.inProject")}
            </h3>
            <ProjectsSelection
              placeholder={t(
                "taskCreation.taskCreationForm.projectSelectionPlaceholder",
              )}
              onSelectProject={(selectedProject) => {
                onProjectChange?.(selectedProject);
              }}
            />
          </div>
        </div>
      )}
      <div className={styles.group}>
        <h3 className={styles.title}>{t("taskCreation.parentListTitle")}</h3>
        <div className={styles.listSelect}>
          <ListSelect
            lists={lists}
            selectedList={list}
            selectList={(nextList) => {
              setList(nextList);
              editForm({ ...formData, startDate: "" });
            }}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.group}>
          <h3 className={styles.title}>{t("taskCreation.nameTitle")}</h3>
          <TextInput
            value={formData.name}
            placeholder={t("taskCreation.namePlaceholder")}
            input={(name) => editForm({ ...formData, name })}
            isInvalid={error.duplicateName || error.emptyName}
            autoFocus
          />
        </div>
        <div className={styles.group}>
          <h3 className={styles.title}>{t("taskCreation.statusTitle")}</h3>
          <TaskStatus
            status={formData.status}
            project={project}
            canAddStatus
            selectStatus={(status) =>
              editForm({
                ...formData,
                status,
              })
            }
          />
        </div>
      </div>
      <div className={styles.dateGroup}>
        <div className={styles.row}>
          <h3 className={styles.title}>{t("taskCreation.startDateTitle")}</h3>
          <div className={styles.dateTimeInput}>
            <DateTimePicker
              dateTime={startDate}
              projectTimezone={project.timeZone}
              placeholder={t("taskCreation.startDatePlaceholder")}
              initialCalendarDate={
                list.startDate
                  ? toDateType(list.startDate)
                  : list.minStartDate
                    ? toDateType(list.minStartDate)
                    : undefined
              }
              input={(date) => {
                const nextDate = new Date(date);
                const currentEndDate = new Date(endDate || new Date());

                const newEndDate = new Date(nextDate);
                newEndDate.setHours(newEndDate.getHours() + 1);

                if (
                  start.getDate() !== nextDate.getDate() ||
                  start.getHours() !== nextDate.getHours() ||
                  start.getMinutes() !== nextDate.getMinutes()
                ) {
                  const shouldUpdateEndDate =
                    !endDate || nextDate >= currentEndDate;

                  editForm({
                    ...formData,
                    startDate: nextDate.toISOString(),
                    ...(shouldUpdateEndDate && {
                      endDate: newEndDate.toISOString(),
                    }),
                  });
                } else {
                  setStartDate(nextDate.toISOString());
                }
              }}
              minDate={
                !!list.startDate
                  ? {
                      year: new Date(list.startDate).getFullYear(),
                      month: new Date(list.startDate).getMonth(),
                      day: new Date(list.startDate).getDate(),
                    }
                  : !!list.minStartDate
                    ? {
                        year: new Date(list.minStartDate).getFullYear(),
                        month: new Date(list.minStartDate).getMonth(),
                        day: new Date(list.minStartDate).getDate(),
                      }
                    : undefined
              }
              clear={() => editForm({ ...formData, startDate: "" })}
              isInvalid={error.startDateOutOfBound || error.invalidDateRange}
            />
          </div>
        </div>
        <div className={styles.row}>
          <h3 className={styles.title}>{t("taskCreation.endDateTitle")}</h3>
          <div className={styles.dateTimeInput}>
            <DateTimePicker
              dateTime={endDate}
              projectTimezone={project.timeZone}
              placeholder={t("taskCreation.endDatePlaceholder")}
              initialCalendarDate={
                list.endDate
                  ? toDateType(list.endDate)
                  : list.maxEndDate
                    ? toDateType(list.maxEndDate)
                    : undefined
              }
              input={(date) => {
                const nextDate = new Date(date);

                end.getDate() !== nextDate.getDate() ||
                end.getHours() !== nextDate.getHours() ||
                end.getMinutes() !== nextDate.getMinutes()
                  ? editForm({
                      ...formData,
                      endDate: nextDate.toISOString(),
                    })
                  : setEndDate(nextDate.toISOString());
              }}
              isInvalid={error.invalidDateRange}
              clear={() => editForm({ ...formData, endDate: "" })}
            />
          </div>
        </div>
      </div>
      <ParticipantsList
        project={project}
        participants={[...formData.allocations.map((elem) => elem.resource)]}
        select={(resources) => {
          editAllocation([
            ...formData.allocations,
            ...resources.map((resource) => ({ ...allocation, resource })),
          ]);
        }}
        remove={(resources) => {
          editAllocation([
            ...formData.allocations.filter(
              (allocation) =>
                !resources.find(
                  (resource) => allocation.resource.uuid === resource.uuid,
                ),
            ),
          ]);
        }}
      />
      <div className={styles.group}>
        <h3 className={styles.title}>{t("taskCreation.descriptionTitle")}</h3>
        <RichTextInput
          value={formData.description}
          maxLength={10000}
          placeholder={t("taskCreation.descriptionPlaceholder")}
          input={(description) => editForm({ ...formData, description })}
        />
      </div>
    </div>
  );
}

export { TaskCreationForm };
